import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Users, Gamepad2, Activity, UserCheck } from "lucide-react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { adminAPI } from "../services/api";
import StatCard from "../components/StatCard";
import { Card, CardHeader, CardTitle, CardContent } from "../components/Card";
import LoadingSpinner from "../components/LoadingSpinner";

const Dashboard = () => {
  // Fetch dashboard stats
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["dashboardStats"],
    queryFn: adminAPI.getDashboardStats,
  });

  // Fetch user growth data
  const { data: userGrowth, isLoading: growthLoading } = useQuery({
    queryKey: ["userGrowth", 30],
    queryFn: () => adminAPI.getUserGrowth(30),
  });

  // Fetch game activity data
  const { data: gameActivity, isLoading: activityLoading } = useQuery({
    queryKey: ["gameActivity", 7],
    queryFn: () => adminAPI.getGameActivity(7),
  });

  // Fetch rating distribution
  const { data: ratingDist, isLoading: ratingLoading } = useQuery({
    queryKey: ["ratingDistribution"],
    queryFn: adminAPI.getRatingDistribution,
  });

  if (statsLoading) {
    return <LoadingSpinner text="Loading dashboard..." />;
  }

  // Prepare data for charts
  const timeControlData = stats?.games?.byTimeControl
    ? Object.entries(stats.games.byTimeControl).map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value,
      }))
    : [];

  const COLORS = ["#FF5E9F", "#FFB101", "#00D2D3", "#FF4757"];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-text-primary">Dashboard</h1>
        <p className="text-text-secondary mt-1">
          Welcome to nearChess Admin Panel
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Users"
          value={stats?.users?.total || 0}
          subtitle={`${stats?.users?.today || 0} new today`}
          icon={Users}
          gradient={true}
        />
        <StatCard
          title="Total Games"
          value={stats?.games?.total || 0}
          subtitle={`${stats?.games?.today || 0} games today`}
          icon={Gamepad2}
        />
        <StatCard
          title="Active Users"
          value={stats?.users?.active || 0}
          subtitle={`${stats?.users?.online || 0} online now`}
          icon={Activity}
        />
        <StatCard
          title="Avg Games/Day"
          value={stats?.games?.avgPerDay || 0}
          subtitle={`${stats?.games?.thisWeek || 0} this week`}
          icon={UserCheck}
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Growth Chart */}
        <Card>
          <CardHeader>
            <CardTitle>User Growth (Last 30 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            {growthLoading ? (
              <LoadingSpinner size="sm" text="" />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={userGrowth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#555555" />
                  <XAxis
                    dataKey="date"
                    stroke="#B0B0B0"
                    tick={{ fill: "#B0B0B0" }}
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return `${date.getMonth() + 1}/${date.getDate()}`;
                    }}
                  />
                  <YAxis stroke="#B0B0B0" tick={{ fill: "#B0B0B0" }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1A1A1F",
                      border: "1px solid #555555",
                      borderRadius: "8px",
                      color: "#FFFFFF",
                    }}
                  />
                  <Legend wrapperStyle={{ color: "#B0B0B0" }} />
                  <Line
                    type="monotone"
                    dataKey="cumulative"
                    stroke="#FF5E9F"
                    strokeWidth={2}
                    name="Total Users"
                  />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#00D2D3"
                    strokeWidth={2}
                    name="New Users"
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Game Activity Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Game Activity (Last 7 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            {activityLoading ? (
              <LoadingSpinner size="sm" text="" />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={gameActivity}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#555555" />
                  <XAxis
                    dataKey="date"
                    stroke="#B0B0B0"
                    tick={{ fill: "#B0B0B0" }}
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return `${date.getMonth() + 1}/${date.getDate()}`;
                    }}
                  />
                  <YAxis stroke="#B0B0B0" tick={{ fill: "#B0B0B0" }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1A1A1F",
                      border: "1px solid #555555",
                      borderRadius: "8px",
                      color: "#FFFFFF",
                    }}
                  />
                  <Legend wrapperStyle={{ color: "#B0B0B0" }} />
                  <Bar dataKey="count" fill="#FFB101" name="Games Played" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Games by Time Control */}
        <Card>
          <CardHeader>
            <CardTitle>Games by Time Control</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={timeControlData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name} ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {timeControlData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1A1A1F",
                    border: "1px solid #555555",
                    borderRadius: "8px",
                    color: "#FFFFFF",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Rating Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Rating Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {ratingLoading ? (
              <LoadingSpinner size="sm" text="" />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={ratingDist?.overall || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#555555" />
                  <XAxis
                    dataKey="range"
                    stroke="#B0B0B0"
                    tick={{ fill: "#B0B0B0" }}
                  />
                  <YAxis stroke="#B0B0B0" tick={{ fill: "#B0B0B0" }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1A1A1F",
                      border: "1px solid #555555",
                      borderRadius: "8px",
                      color: "#FFFFFF",
                    }}
                  />
                  <Bar dataKey="count" fill="#00D2D3" name="Users" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
